import { useState } from "react";
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { clientApi } from "../lib/api";
import { toast } from "sonner";

export const ChangePasswordModal = ({ open, onOpenChange, isFirstTime = false, blocking = false, onSuccess }) => {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setShow(false);
  };

  const valid =
    newPwd.length >= 8 &&
    newPwd === confirmPwd &&
    (isFirstTime || currentPwd.length >= 6);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await clientApi.post("/portal/change-password", {
        new_password: newPwd,
        current_password: currentPwd || null,
      });
      toast.success("Contraseña actualizada con éxito", { duration: 2500 });
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo cambiar la contraseña";
      toast.error(typeof msg === "string" ? msg : "Error al cambiar contraseña");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (blocking && !o) return; if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        className="max-w-md"
        data-testid="change-password-modal"
        onPointerDownOutside={(e) => { if (blocking) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (blocking) e.preventDefault(); }}
        onInteractOutside={(e) => { if (blocking) e.preventDefault(); }}
        hideClose={blocking}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-cm-red" />
            {blocking
              ? "Crea tu contraseña para continuar"
              : isFirstTime
                ? "Crea tu contraseña personal"
                : "Cambiar contraseña"}
          </DialogTitle>
          <DialogDescription className="text-cm-ink-soft">
            {blocking
              ? "Por seguridad, cuando llevas 2 o más ingresos con la contraseña genérica te pedimos crear una propia. Mínimo 8 caracteres."
              : isFirstTime
                ? "Reemplaza la contraseña genérica por una propia para mayor seguridad. Mínimo 8 caracteres."
                : "Ingresa tu contraseña actual y la nueva. Mínimo 8 caracteres."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          {!isFirstTime && (
            <div>
              <label className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium block mb-1.5">
                Contraseña actual
              </label>
              <Input
                type={show ? "text" : "password"}
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                placeholder="Tu contraseña actual"
                required
                data-testid="current-password-input"
                className="border-cm-border"
              />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium block mb-1.5">
              Nueva contraseña
            </label>
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                data-testid="new-password-input"
                className="border-cm-border pr-10"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-cm-ink-soft hover:text-cm-ink"
                tabIndex={-1}
                aria-label={show ? "Ocultar" : "Mostrar"}
                data-testid="toggle-password-visibility"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newPwd && newPwd.length < 8 && (
              <p className="text-xs text-red-600 mt-1">Faltan {8 - newPwd.length} caracteres</p>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium block mb-1.5">
              Confirmar nueva contraseña
            </label>
            <Input
              type={show ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Repítela"
              required
              data-testid="confirm-password-input"
              className="border-cm-border"
            />
            {confirmPwd && newPwd !== confirmPwd && (
              <p className="text-xs text-red-600 mt-1">Las contraseñas no coinciden</p>
            )}
            {confirmPwd && newPwd === confirmPwd && newPwd.length >= 8 && (
              <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Coinciden
              </p>
            )}
          </div>

          <div className="bg-cm-cream-alt/60 border border-cm-border rounded p-3 text-xs text-cm-ink-soft">
            <p className="font-medium text-cm-ink mb-1">¿Olvidaste tu contraseña personal?</p>
            <p>
              Escribe por WhatsApp a <span className="font-mono">304 5729 333</span> y solicita restablecer.
              Podrás volver a entrar con la contraseña genérica.
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            {!isFirstTime && (
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="cancel-password-change">
                Cancelar
              </Button>
            )}
            <Button
              type="submit"
              disabled={!valid || submitting}
              className="btn-cm-primary"
              data-testid="submit-password-change"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                "Guardar contraseña"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
